import { Controller } from "@hotwired/stimulus"

// Push notifications: registra el service worker, maneja suscripciones
// y muestra el banner de opt-in en las páginas de noticias.
export default class extends Controller {
  static values = {
    vapidPublicKey: String,
    snoozeDays: { type: Number, default: 7 },
    delayMs: { type: Number, default: 3000 }
  }
  static targets = ["modal", "card", "subscribeButton", "unsubscribeButton"]

  connect() {
    this.registration = null
    this.subscribed = false
    this.onKeydown = this.handleKeydown.bind(this)

    if (!this.supported()) {
      console.warn("[Push] Navegador sin soporte push (serviceWorker/PushManager/Notification) o contexto no seguro")
      this.hideSetupTargets()
      return
    }
    if (!this.vapidPublicKeyValue) {
      console.warn("[Push] VAPID_PUBLIC_KEY no configurada")
      this.hideSetupTargets()
      return
    }

    navigator.serviceWorker.register("/push-sw.js")
      .then((registration) => {
        this.registration = registration
        return this.refreshState()
      })
      .then(() => this.maybeShowModal())
      .catch((error) => {
        console.warn("[Push] Error registrando service worker:", error)
        this.hideSetupTargets()
      })
  }

  disconnect() {
    if (this.modalTimeout) clearTimeout(this.modalTimeout)
    document.removeEventListener("keydown", this.onKeydown)
  }

  supported() {
    return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window
  }

  async refreshState() {
    if (!this.registration) return
    const subscription = await this.registration.pushManager.getSubscription()
    this.subscribed = !!subscription
    this.updateButtons()
  }

  updateButtons() {
    const denied = Notification.permission === "denied"
    const showCard = !this.subscribed && !denied

    this.cardTargets.forEach((card) => {
      card.classList.toggle("hidden", !showCard)
    })
    this.subscribeButtonTargets.forEach((button) => {
      button.classList.toggle("hidden", !showCard)
    })
    this.unsubscribeButtonTargets.forEach((button) => {
      button.classList.toggle("hidden", !this.subscribed)
    })
  }

  async subscribe() {
    if (!this.registration || this.subscribed) return

    try {
      const permission = await Notification.requestPermission()
      if (permission !== "granted") {
        this.updateButtons()
        this.hideModal()
        return
      }

      const subscription = await this.registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(this.vapidPublicKeyValue)
      })

      await this.postSubscription(subscription)
      this.subscribed = true
      this.updateButtons()
      this.hideModal()
    } catch (error) {
      console.warn("[Push] Error al suscribirse:", error)
    }
  }

  async unsubscribe() {
    if (!this.registration) return

    try {
      const subscription = await this.registration.pushManager.getSubscription()
      if (subscription) {
        await subscription.unsubscribe()
        await this.deleteSubscription(subscription.endpoint)
      }
      this.subscribed = false
      this.updateButtons()
    } catch (error) {
      console.warn("[Push] Error al desuscribirse:", error)
    }
  }

  maybeShowModal() {
    if (!this.hasModalTarget) return
    if (this.subscribed) return
    if (Notification.permission !== "default") return

    const snoozedAt = Number(localStorage.getItem("almagro:push:snoozed-at"))
    if (snoozedAt && (Date.now() - snoozedAt) < this.snoozeDaysValue * 86400000) return

    this.modalTimeout = setTimeout(() => {
      if (document.contains(this.element)) this.showModal()
    }, this.delayMsValue)
  }

  showModal() {
    if (!this.hasModalTarget) return
    const modal = this.modalTarget
    modal.classList.remove("hidden")
    modal.setAttribute("aria-hidden", "false")
    requestAnimationFrame(() => {
      modal.querySelector("[data-push-box]")?.classList.remove("-translate-y-4", "opacity-0")
    })
    document.addEventListener("keydown", this.onKeydown)
  }

  hideModal() {
    if (!this.hasModalTarget) return
    const modal = this.modalTarget
    modal.setAttribute("aria-hidden", "true")
    modal.querySelector("[data-push-box]")?.classList.add("-translate-y-4", "opacity-0")
    document.removeEventListener("keydown", this.onKeydown)
    setTimeout(() => modal.classList.add("hidden"), 300)
  }

  handleKeydown(event) {
    if (event.key === "Escape") this.snooze()
  }

  snooze() {
    localStorage.setItem("almagro:push:snoozed-at", String(Date.now()))
    this.hideModal()
  }

  hideSetupTargets() {
    this.cardTargets.forEach((card) => card.classList.add("hidden"))
    this.subscribeButtonTargets.forEach((button) => button.classList.add("hidden"))
    this.unsubscribeButtonTargets.forEach((button) => button.classList.add("hidden"))
    if (this.hasModalTarget) this.modalTarget.remove()
  }

  async postSubscription(subscription) {
    const response = await fetch("/push/subscriptions", {
      method: "POST",
      headers: this.requestHeaders(),
      body: JSON.stringify({ subscription: subscription.toJSON() })
    })
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
  }

  async deleteSubscription(endpoint) {
    await fetch("/push/subscriptions", {
      method: "DELETE",
      headers: this.requestHeaders(),
      body: JSON.stringify({ endpoint })
    })
  }

  requestHeaders() {
    return {
      "Content-Type": "application/json",
      "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || ""
    }
  }

  urlBase64ToUint8Array(base64String) {
    const padding = "=".repeat((4 - base64String.length % 4) % 4)
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/")
    const rawData = window.atob(base64)
    return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)))
  }
}
